import React, { useEffect, useMemo, useState } from 'react';
import styles from './Closures.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getWorker, getWorkerClosures } from '../../../../store/selectors';
import { salonManagementActions } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { getActiveSalonId } from '../../../../../Auth/store/selectors';
import TitleSectionBack from '../../../../../Common/TitleSectionBack';
import AvatarIcon from '../../../../../Common/AvatarIcon';
import clx from 'classnames';
import useWindowSize from '../../../../../../utils/useWindowSize';
import CustomDatePicker from '../../../../../Common/CustomDatePicker';
import ConfirmDeleteModal from '../../../../../Common/ConfirmDeleteModal';
import { modalActions } from '../../../../../Modals/store';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useTranslation } from 'react-i18next';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';

dayjs.locale('it');

const MOTIVATIONS = [
  'workers.closures.motivations.holiday',
  'workers.closures.motivations.vacation',
  'workers.closures.motivations.noServices',
  'workers.closures.motivations.personal',
];

const DAY_ORDER = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];

const getMotivationLabel = (tipo, t) => {
  const index = Number(tipo);
  if (!Number.isFinite(index) || index < 0 || index >= MOTIVATIONS.length) {
    return t('workers.closures.card.motivation');
  }
  return t(MOTIVATIONS[index]);
};

const Closures = () => {
  // Mobile: bottone nativo in toolbar (niente FAB).
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { workerId } = useParams();
  const navigate = useNavigate();
  const worker = useSelector(getWorker());
  const activeSalonId = useSelector(getActiveSalonId());
  const { isMobile } = useWindowSize();

  const [tablesFilterDate, setTablesFilterDate] = useState(null);
  const closures = useSelector(getWorkerClosures(tablesFilterDate));
  const [closuresDateToDelete, setClosuresDateToDelete] = useState(false);

  const closuresMerged = useMemo(() => {
    return Object.values(closures || {}).map((items) => {
      let startDate;
      let endDate;
      const days = [];
      items.forEach((item) => {
        if (item.data_chiusura < startDate || startDate === undefined) startDate = item.data_chiusura;
        if (item.data_chiusura > endDate || endDate === undefined) endDate = item.data_chiusura;
        const day = dayjs(item.data_chiusura).format('dddd');
        if (!days.includes(day)) days.push(day);
      });
      days.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
      return {
        dates: { startDate, endDate },
        days,
        items,
      };
    });
  }, [closures]);

  useEffect(() => {
    dispatch(salonManagementActions.GET_WORKER.REQUEST(workerId, null, { skipLoader: true }));
    return () => {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
    };
  }, [workerId]);

  useEffect(() => {
    if (worker?.salone && worker?.salone !== activeSalonId) {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
      navigate(-1);
    }
  }, [worker, activeSalonId]);

  const openCreateModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'workerClosuresModal',
        props: { worker: worker?.nome_referente },
      }),
    );
  };

  const addClosureButton = (
    <button type="button" className={styles.add_button} onClick={openCreateModal}>
      <Plus size={16} strokeWidth={2.4} aria-hidden />
      <span>{t('workers.closures.addClosure')}</span>
    </button>
  );

  return (
    <>
      <ConfirmDeleteModal
        isModalOpen={closuresDateToDelete}
        setIsModalOpen={setClosuresDateToDelete}
        title={t('workers.closures.confirmDelete.title')}
        buttons={[t('common.actions.cancel'), t('common.actions.delete')]}
        action={() =>
          closuresDateToDelete.map((date) =>
            dispatch(
              salonManagementActions.DELETE_WORKER_CLOSURES.REQUEST(
                {
                  idPoltrona: workerId,
                  data: date,
                },
                () => setClosuresDateToDelete(false),
              ),
            ),
          )
        }
      />

      <div className={clx(styles.page, { [styles.page_mobile]: isMobile })}>
        <div className={styles.top_bar}>
          <TitleSectionBack
            title={t('workers.closures.title')}
            description={t('workers.closures.description')}
          />
          {!isMobile ? (
            <div className={styles.top_actions}>
              <div className={styles.worker_chip}>
                <AvatarIcon name={worker?.nome_referente} icon={worker?.icon} size="40px" />
                <div className={styles.worker_chip_copy}>
                  <span>{t('workers.closures.modifyingSchedule')}</span>
                  <strong>{worker?.nome_referente || '—'}</strong>
                </div>
              </div>
              {addClosureButton}
            </div>
          ) : null}
        </div>

        {isMobile ? (
          <div className={styles.mobile_toolbar}>
            <div className={styles.worker_chip}>
              <AvatarIcon name={worker?.nome_referente} icon={worker?.icon} size="40px" />
              <div className={styles.worker_chip_copy}>
                <span>{t('workers.closures.modifyingSchedule')}</span>
                <strong>{worker?.nome_referente || '—'}</strong>
              </div>
            </div>
            {addClosureButton}
          </div>
        ) : null}

        <div className={styles.toolbar}>
          <div className={styles.toolbar_copy}>
            <h3>{t('workers.closures.listTitle')}</h3>
            <span>
              {closuresMerged.length}{' '}
              {closuresMerged.length === 1
                ? t('workers.closures.countOne')
                : t('workers.closures.countMany')}
            </span>
          </div>
          <div className={styles.picker_wrap}>
            <CustomDatePicker
              type="button"
              value={tablesFilterDate}
              setValue={setTablesFilterDate}
              placeholder={t('workers.closures.filterByDate')}
              disablePast={false}
            />
          </div>
        </div>

        {closuresMerged.length ? (
          <div className={styles.cards_grid}>
            {closuresMerged.map((item) => {
              const motivation = getMotivationLabel(item.items?.[0]?.tipo_chiusura, t);
              const sameDay = item.dates.startDate === item.dates.endDate;
              return (
                <article key={`${item.dates.startDate}-${item.dates.endDate}`} className={styles.card}>
                  <div className={styles.card_icon} aria-hidden>
                    <CalendarOff size={18} strokeWidth={2.2} />
                  </div>
                  <div className={styles.card_main}>
                    <div className={styles.card_title_row}>
                      <h4>
                        {sameDay
                          ? dayjs(item.dates.startDate).format('DD MMM YYYY')
                          : `${dayjs(item.dates.startDate).format('DD MMM')} – ${dayjs(item.dates.endDate).format('DD MMM YYYY')}`}
                      </h4>
                      <span className={styles.badge}>{t('workers.closures.card.allDay')}</span>
                    </div>
                    <div className={styles.meta}>
                      <span className={styles.pill}>{motivation}</span>
                      <span className={clx(styles.pill, styles.pill_muted)}>
                        {item.days.map((day) => day.charAt(0).toUpperCase() + day.slice(1)).join(' · ')}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.delete_btn}
                    onClick={() =>
                      setClosuresDateToDelete(item.items.map(({ data_chiusura }) => data_chiusura))
                    }
                  >
                    <Trash2 size={15} strokeWidth={2.3} aria-hidden />
                    {t('workers.closures.card.deleteClosure')}
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty_state}>
            <CalendarOff size={22} strokeWidth={2.2} aria-hidden />
            <strong>{t('workers.closures.noExtraClosures')}</strong>
            {addClosureButton}
          </div>
        )}
      </div>
    </>
  );
};

export default Closures;
